import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, LogIn, Loader2, Truck, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransporterLoginPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    login: '',
    password: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.login || !formData.password) {
      setError(t('transporter.enterBothFields'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/transporters/login`, formData);
      // Store login info (in production, use proper auth tokens)
      sessionStorage.setItem('transporter', JSON.stringify(response.data.transporter));
      navigate('/drivers/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || t('errors.submitFailed');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = prompt(t('errors.enterEmail'));
    if (!email) return;

    try {
      await axios.post(`${API}/transporters/resend-verification`, { email });
      alert(t('tripSuccess.confirmationEmail'));
    } catch (err) {
      alert(err.response?.data?.detail || t('errors.submitFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-md mx-auto px-4 relative z-10">
          <button
            onClick={() => navigate('/drivers')}
            className="flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-8"
            data-testid="back-to-transporters-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('transporter.backToTransporters')}</span>
          </button>

          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">{t('transporter.loginTitle')}</h1>
              <p className="text-gray-400">{t('transporter.welcomeBack')}</p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                <p className="text-red-500 text-sm text-center">{error}</p>
                {error.includes('verify') && (
                  <button
                    onClick={handleResendVerification}
                    className="text-amber-500 text-sm underline w-full mt-2 hover:text-amber-400"
                    data-testid="resend-verification-btn"
                  >
                    {t('transporter.resendVerification')}
                  </button>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="transporter-login-form">
              <div>
                <Label className="text-gray-300 mb-2 block">{t('transporter.userId')}</Label>
                <Input
                  name="login"
                  value={formData.login}
                  onChange={handleInputChange}
                  placeholder="email@example.com or HG-TR-XXXXXX"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="login-input"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('auth.password')}
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="text-right mt-2">
                  <Link
                    to="/drivers/forgot-password"
                    className="text-amber-500 text-sm hover:text-amber-400 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg"
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('transporter.loggingIn')}
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    {t('transporter.login')}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-400 mb-4">{t('transporter.noAccountQuestion')}</p>
              <Link
                to="/drivers"
                className="text-amber-500 font-semibold hover:text-amber-400 transition-colors"
                data-testid="register-link"
              >
                {t('common.registerYourBusiness')} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TransporterLoginPage;
